/**
 * Single definition of "the editor left this empty" for content coming out of a
 * Builder data model, so every landing section falls back the same way.
 *
 * Builder sends a cleared text field as an empty string rather than omitting it,
 * and a cleared list as an empty array — which is why `??` is not enough here.
 */

type PlainObject = Record<string, unknown>;

export const isBlank = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const isPlainObject = (value: unknown): value is PlainObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Fills every blank field in `data` with the matching field from `defaults`,
 * one nesting level deep so grouped fields (a `cta` with its label and href)
 * fall back field by field instead of as a whole object.
 *
 * `defaults` drives the result: a key absent from it is dropped, so a section
 * can never render a field it has no default for.
 */
export default function withBuilderDefaults<T extends object>(
  data: Partial<T> | null | undefined,
  defaults: T
): T {
  const merged: PlainObject = {};
  const provided = data as PlainObject | null | undefined;

  for (const [key, defaultValue] of Object.entries(defaults)) {
    const value = provided?.[key];

    if (isPlainObject(defaultValue)) {
      merged[key] = withBuilderDefaults(
        isPlainObject(value) ? value : null,
        defaultValue
      );
      continue;
    }

    merged[key] = isBlank(value) ? defaultValue : value;
  }

  return merged as T;
}

/**
 * Drops list items that are missing any of the fields the design needs, so a
 * half-filled entry is left out instead of rendering blank.
 *
 * Lists are never merged item by item: a creator's name and photo belong
 * together, and completing one from the defaults would caption a real photo
 * with somebody else's name. Filtering can empty the list, which then falls
 * back to the defaults through `withBuilderDefaults`.
 */
export function keepCompleteItems<T extends object>(
  items: T[] | null | undefined,
  requiredKeys: (keyof T)[]
): T[] {
  if (!Array.isArray(items)) return [];

  return items.filter((item) =>
    requiredKeys.every((key) => !isBlank(item?.[key]))
  );
}
